import type { Event } from '@bugsnag/js';

// Distinctive marker of the Session Summary /finished poll catch wrapper (useSessionDataFetch.ts).
// The catch wraps the AxiosError into an 'ApplicationError' string, so the generic AxiosError/'Network
// Error' filter below never matches — this marker lets us re-detect the wrapped poll failure. [sc-1089337]
const SESSION_FINISHED_POLL_MARKER = 'trying to get session details from';

// Retry-exhausted transient signatures on the /finished poll. adaptiveHttp already retries ERR_NETWORK,
// ECONNABORTED and 503 up to 3x before they surface here, and the poll self-recovers on the next tick,
// so these are expected environmental noise — not application defects. Deliberately excludes 500/502/504,
// which may indicate a genuine upstream fault worth reporting. [sc-1089337 unifies sc-1077621 + sc-1005064]
const FINISHED_POLL_TRANSIENT_SIGNATURES = ['Network Error', 'ERR_NETWORK', 'ECONNABORTED', 'status code 503'];

export const bugsnagIgnoreErrors = (event: Event) => {
  const errorMessage = event.errors[0]?.errorMessage || '';
  const errorClass = event.errors[0]?.errorClass || '';

  // Ignore third-party script errors
  if (errorMessage.includes("evaluating 'o.innerText.trim'")) {
    return false;
  }

  // Ignore network errors (user connectivity issues)
  if (errorClass === 'AxiosError' && errorMessage === 'Network Error') {
    return false;
  }

  // Ignore retry-exhausted transient failures on the Session Summary /finished poll. The poll wraps its
  // AxiosError into an ApplicationError string, so it evades the AxiosError filter above. Scoped by the
  // poll marker + a transient signature so genuine non-transient failures on this poll are still reported.
  // [sc-1089337] See docs/claude/sc-1089337-session-summary-finished-transient-suppression.md
  if (
    errorMessage.includes(SESSION_FINISHED_POLL_MARKER) &&
    FINISHED_POLL_TRANSIENT_SIGNATURES.some(signature => errorMessage.includes(signature))
  ) {
    return false;
  }

  // Ignore timeout errors
  if (errorMessage.includes('timeout of') && errorMessage.includes('exceeded')) {
    return false;
  }

  // Ignore EVSE not found errors
  if (errorMessage.includes('No EVSE with id') && errorMessage.includes('was found!')) {
    return false;
  }

  // Ignore unhandled rejection non-error events
  if (errorMessage.includes('unhandledRejection handler received a non-error')) {
    return false;
  }

  // Ignore Stripe element unmount errors
  if (errorMessage.includes('We could not retrieve data from the specified Element')) {
    return false;
  }

  // Ignore iOS Safari Stripe SDK getComputedStyle timing race condition
  // This is a transient browser-specific issue in Stripe's Shadow DOM implementation on iOS Safari
  // See: docs/claude/sc-326966-ios-safari-stripe-getcomputedstyle.md
  if (errorMessage.includes('getComputedStyle') && errorMessage.includes('must be an instance of Element')) {
    return false;
  }

  // Ignore Chrome extension errors (browser extension API calls)
  // These errors originate from user's installed browser extensions, not our code
  // Common examples: "Invalid call to runtime.sendMessage(). Tab not found."
  // See: docs/claude/sc-311712-browser-extension-errors.md
  if (
    errorMessage.includes('runtime.sendMessage') ||
    (errorClass === 'Error' && errorMessage.includes('Tab not found') && errorMessage.includes('runtime'))
  ) {
    return false;
  }

  return true;
};
